import React, { useState, useEffect, useRef } from 'react';

// Hook personalizado para animación de conteo
const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Función de easing para suavizar la animación
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = start + (end - start) * easeOutQuart;
      
      // Para números decimales, mantener un decimal
      if (end % 1 !== 0) {
        setCount(Math.round(currentCount * 10) / 10);
      } else {
        setCount(Math.floor(currentCount));
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);

  return { count, ref };
};

const stats = [
  { value: 200, suffix: '+', label: 'Infrastructure deployments' },
  { value: 87, suffix: '%', label: 'Deployment time reduction' },
  { value: 20, suffix: '+', label: 'Companies have used our platform' },
  { value: 99.9, suffix: '%', label: 'Guaranteed uptime' },
];

const audiences = [
  {
    title: 'Freelancers & Consultants',
    description:
      'Deliver infrastructure projects faster and demonstrate value with professional visualizations.',
    points: ['Reduce setup time 80%', 'Impress clients', 'Bill more projects'],
  },
  {
    title: 'Technology Consultancies',
    description:
      'Standardize deliverables, reduce errors and scale your infrastructure team.',
    points: ['Reusable templates', 'Faster onboarding', 'Higher margin'],
  },
  {
    title: 'Startups & Scale-ups',
    description:
      'Build robust infrastructure from day one without hiring specialists.',
    points: ['Zero DevOps team needed', 'Predictable costs', 'Scale when ready'],
  },
  {
    title: 'Enterprise Teams',
    description:
      'Accelerate digital transformation with automated governance and compliance.',
    points: ['Multi-cloud strategy', 'Security by design', 'Audit trails'],
  },
];

const AudienceIcons = [
  // Desarrollador (SVG proporcionado)
  (
    <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 md:w-6 md:h-6">
      <g transform="translate(0,512) scale(0.1,-0.1)" fill="currentColor" stroke="none">
        <path d="M2414 4620 c-312 -41 -584 -264 -684 -562 -183 -546 219 -1108 793 -1108 173 0 325 46 466 141 101 68 161 127 230 228 92 134 141 295 141 464 0 239 -90 449 -264 614 -128 122 -294 200 -476 222 -93 12 -117 12 -206 1z"/>
        <path d="M1940 2937 c-82 -16 -253 -68 -352 -108 -199 -79 -430 -209 -487 -274 -73 -83 -80 -111 -186 -725 -67 -388 -76 -450 -66 -485 21 -78 45 -100 231 -225 100 -67 188 -124 196 -127 12 -4 14 77 14 589 l0 594 26 53 c32 64 86 106 157 121 37 8 351 10 1082 8 l1030 -3 40 -22 c52 -28 89 -68 114 -123 20 -44 21 -63 21 -617 0 -315 3 -573 6 -573 4 0 94 55 201 122 211 133 233 154 233 229 0 24 -38 266 -84 537 -92 539 -98 561 -180 651 -117 127 -500 306 -791 370 l-80 18 -70 -38 c-279 -151 -621 -156 -911 -12 -93 46 -102 49 -144 40z"/>
        <path d="M1453 2229 c-64 -41 -63 -29 -63 -759 l0 -660 -200 0 -200 0 0 -49 c0 -59 30 -108 80 -133 33 -17 123 -18 1455 -18 1332 0 1422 1 1454 18 49 25 71 60 78 125 l6 57 -201 0 -201 0 -3 673 c-3 638 -4 674 -22 703 -41 68 37 64 -1113 64 -1025 0 -1038 -1 -1070 -21z m1194 -475 c24 -9 43 -19 43 -23 0 -7 -238 -583 -243 -588 -2 -2 -25 6 -50 17 -37 17 -45 24 -39 38 55 140 238 572 241 572 3 0 25 -7 48 -16z m-427 -29 l34 -35 -109 -110 -110 -110 110 -110 110 -110 -38 -37 -37 -38 -147 148 -148 147 145 145 c80 80 147 145 150 145 4 0 21 -16 40 -35z m800 -110 l145 -145 -148 -147 -147 -148 -37 38 -38 37 110 110 110 110 -110 110 -109 110 34 35 c19 19 36 35 40 35 3 0 70 -65 150 -145z"/>
      </g>
    </svg>
  ),
  // Agencia (SVG proporcionado actualizado)
  (
    <svg viewBox="0 0 200 199" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 md:w-6 md:h-6">
      <g transform="translate(0,199) scale(0.1,-0.1)" fill="currentColor" stroke="none">
        <path d="M910 1966 c0 -29 -9 -36 -79 -64 -66 -27 -86 -27 -111 -2 -11 11 -23 20 -27 20 -5 0 -36 -27 -71 -61 -66 -64 -68 -72 -24 -113 l21 -18 -26 -57 c-14 -31 -29 -63 -33 -71 -4 -8 -22 -16 -41 -18 -54 -6 -59 -15 -59 -113 l0 -89 46 0 c43 0 46 -2 60 -37 8 -21 23 -55 35 -77 l21 -38 -33 -34 -33 -34 67 -67 67 -67 35 34 c22 21 39 31 43 24 8 -12 108 -54 128 -54 10 0 14 -14 14 -45 l0 -45 95 0 95 0 0 45 c0 37 3 45 19 45 10 0 45 13 77 30 l59 29 33 -31 32 -32 65 64 c36 35 65 70 65 76 0 7 -12 21 -27 32 -27 19 -27 22 -15 54 8 18 22 53 31 78 16 43 19 45 61 50 l45 5 3 83 c3 96 -5 112 -55 112 -30 0 -36 4 -49 38 -8 20 -24 53 -34 73 l-19 36 29 28 c17 16 30 33 30 39 0 13 -111 126 -124 126 -6 0 -22 -11 -36 -25 l-27 -26 -81 33 c-74 30 -82 35 -82 61 l0 27 -95 0 c-93 0 -95 0 -95 -24z m197 -243 c62 -19 113 -64 145 -126 32 -62 30 -175 -3 -241 -44 -89 -120 -135 -232 -141 -53 -3 -127 13 -127 27 0 5 -4 6 -10 3 -17 -10 -80 46 -111 99 -25 45 -29 61 -29 130 0 96 22 149 84 203 71 62 178 79 283 46z"/>
        <path d="M1088 1582 c-18 -15 -42 -38 -53 -52 -11 -13 -30 -36 -41 -49 l-21 -25 -39 37 c-22 20 -46 37 -55 37 -19 0 -42 -24 -45 -47 -3 -22 112 -133 138 -133 12 0 55 34 110 87 81 80 89 90 86 122 -4 49 -37 59 -80 23z"/>
        <path d="M381 978 c-63 -14 -134 -87 -150 -154 -19 -80 0 -154 53 -207 145 -145 391 -37 373 164 -6 64 -24 104 -67 148 -44 44 -136 66 -209 49z"/>
        <path d="M1540 983 c-59 -10 -91 -27 -128 -67 -68 -73 -79 -158 -34 -247 37 -74 95 -112 177 -117 156 -10 265 129 221 280 -29 101 -133 168 -236 151z"/>
        <path d="M911 863 c-124 -65 -165 -215 -87 -318 63 -82 150 -118 233 -94 65 18 147 97 163 159 26 95 -28 213 -117 255 -66 32 -130 31 -192 -2z"/>
        <path d="M382 520 c-120 -18 -256 -122 -308 -235 -31 -67 -41 -147 -21 -163 11 -9 78 -12 262 -10 l247 3 21 55 c25 69 81 155 119 183 37 27 35 39 -9 73 -21 15 -41 33 -45 38 -4 6 -8 7 -8 3 0 -5 -13 1 -30 13 -24 17 -150 53 -170 49 -3 -1 -29 -5 -58 -9z"/>
        <path d="M1515 520 c-22 -5 -53 -14 -70 -20 -16 -6 -32 -11 -35 -11 -3 0 -13 -8 -22 -16 -10 -9 -18 -13 -18 -9 0 4 -10 -3 -22 -15 -13 -11 -33 -28 -45 -36 -31 -20 -29 -35 6 -61 42 -30 91 -106 117 -177 l22 -60 247 -3 c185 -2 251 1 262 10 22 18 9 106 -23 170 -30 60 -94 131 -149 167 -22 14 -42 26 -45 27 -3 0 -18 6 -35 13 -26 11 -131 33 -145 30 -3 -1 -23 -5 -45 -9z"/>
        <path d="M886 400 c-146 -47 -253 -169 -272 -311 -11 -83 -32 -79 390 -79 204 0 377 4 385 9 20 13 9 103 -22 171 -79 178 -292 270 -481 210z"/>
      </g>
    </svg>
  ),
  // Cohete (SVG proporcionado actualizado)
  (
    <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 md:w-6 md:h-6">
      <g transform="translate(0,512) scale(0.1,-0.1)" fill="currentColor" stroke="none">
        <path d="M4320 5109 c-170 -13 -441 -53 -602 -89 -306 -69 -533 -148 -803 -280 -632 -308 -1259 -918 -1634 -1589 l-53 -94 914 -914 914 -914 117 65 c651 365 1261 995 1567 1621 142 289 228 546 296 882 58 290 77 486 78 818 l1 290 -28 57 c-31 64 -70 101 -137 132 -41 18 -68 20 -275 22 -126 1 -286 -2 -355 -7z m-630 -876 c267 -65 481 -281 547 -551 25 -102 22 -267 -6 -366 -71 -253 -260 -449 -506 -526 -108 -34 -276 -39 -385 -11 -150 38 -281 112 -377 213 -340 361 -242 950 196 1177 169 87 349 109 531 64z"/>
        <path d="M3408 3944 c-164 -39 -291 -161 -333 -318 -74 -273 120 -546 400 -564 238 -14 435 142 477 378 40 229 -130 466 -366 509 -75 14 -106 13 -178 -5z"/>
        <path d="M1056 3720 c-231 -19 -284 -29 -348 -61 -83 -43 -129 -100 -395 -503 -146 -220 -274 -418 -286 -440 -16 -31 -22 -59 -22 -116 0 -65 4 -82 29 -128 35 -62 87 -107 148 -127 34 -12 412 -75 446 -75 5 0 24 62 42 138 99 414 276 807 542 1199 95 140 91 133 76 132 -7 -1 -111 -10 -232 -19z"/>
        <path d="M1057 2661 c-77 -214 -143 -499 -134 -577 10 -89 29 -112 549 -631 490 -488 505 -501 561 -519 52 -16 67 -17 144 -5 116 16 323 70 462 120 63 23 117 43 120 46 5 6 -1649 1665 -1660 1665 -4 0 -23 -44 -42 -99z"/>
        <path d="M585 1679 c-11 -6 -143 -133 -293 -283 -296 -295 -304 -305 -282 -385 13 -48 66 -97 114 -106 76 -15 85 -8 370 274 148 146 274 280 282 298 33 76 4 160 -66 195 -39 20 -95 23 -125 7z"/>
        <path d="M3625 1223 c-407 -277 -822 -463 -1257 -563 -54 -12 -98 -27 -98 -32 0 -34 63 -411 75 -446 20 -61 65 -113 127 -148 46 -25 63 -29 128 -29 57 0 85 6 116 22 23 12 221 141 440 286 462 305 496 336 533 474 9 34 52 503 47 510 -1 1 -51 -32 -111 -74z"/>
        <path d="M1025 1224 c-43 -22 -983 -962 -1006 -1006 -10 -21 -19 -51 -19 -68 0 -50 35 -107 80 -130 49 -25 90 -25 138 -1 20 11 256 239 524 508 525 526 510 509 494 597 -17 92 -127 144 -211 100z"/>
        <path d="M1465 771 c-16 -10 -148 -137 -292 -282 -221 -224 -262 -270 -268 -301 -21 -111 77 -207 182 -179 54 15 587 549 598 599 14 69 -16 136 -75 167 -40 21 -109 19 -145 -4z"/>
      </g>
    </svg>
  ),
  // Globo con edificios
  (
    <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 md:w-6 md:h-6">
      <g transform="translate(0,512) scale(0.1,-0.1)" fill="currentColor" stroke="none">
        <path d="M3450 4698 c0 -187 -12 -209 -135 -248 -22 -7 -72 -28 -112 -46 -74 -35 -128 -42 -166 -23 -12 6 -66 55 -120 107 l-97 97 -107 -108 -108 -107 97 -97 c52 -54 101 -108 107 -120 19 -38 12 -88 -24 -166 -19 -42 -35 -80 -35 -86 0 -7 59 -11 179 -11 l179 0 41 54 c105 136 273 217 449 218 169 0 292 -51 412 -172 119 -118 172 -243 172 -405 0 -263 -169 -485 -427 -561 -93 -27 -231 -23 -328 10 -42 14 -95 37 -117 51 -22 14 -42 25 -45 25 -3 0 -5 -67 -5 -150 l0 -150 555 -2 c512 -3 556 -4 572 -20 12 -13 19 -41 23 -99 l5 -81 94 97 95 96 -96 97 c-53 53 -101 107 -107 119 -21 41 -12 98 28 185 21 45 47 105 57 133 13 33 31 57 53 72 31 21 45 23 177 23 l144 0 0 155 0 155 -144 0 c-132 0 -146 2 -177 23 -22 15 -40 39 -53 72 -10 28 -36 88 -57 133 -40 87 -49 144 -28 185 6 12 54 66 107 119 l96 98 -107 107 -107 107 -98 -96 c-53 -53 -107 -101 -119 -107 -41 -21 -98 -12 -185 28 -45 21 -105 47 -133 57 -33 13 -57 31 -72 53 -21 31 -23 45 -23 177 l0 144 -155 0 -155 0 0 -142z"/>
        <path d="M1430 2015 l0 -1755 243 2 242 3 2 360 c3 353 3 360 24 378 19 15 51 17 344 17 293 0 325 -2 344 -17 21 -18 21 -25 24 -378 l2 -360 243 -3 242 -2 0 1755 0 1755 -855 0 -855 0 0 -1755z m1332 1368 c17 -15 18 -39 18 -336 0 -284 -2 -322 -17 -339 -15 -17 -43 -18 -476 -18 -414 0 -462 2 -479 17 -17 15 -18 39 -18 336 0 284 2 322 17 339 15 17 43 18 476 18 414 0 462 -2 479 -17z m0 -1030 c25 -23 23 -68 -4 -87 -20 -14 -81 -16 -473 -16 -392 0 -453 2 -473 16 -26 18 -29 60 -5 86 15 17 43 18 476 18 414 0 462 -2 479 -17z m0 -330 c24 -21 23 -58 -2 -83 -19 -19 -33 -20 -473 -20 -393 0 -455 2 -475 16 -26 18 -29 60 -5 86 15 17 43 18 476 18 414 0 462 -2 479 -17z m0 -330 c22 -19 23 -52 4 -79 -14 -18 -32 -19 -475 -22 -451 -3 -461 -2 -481 18 -24 24 -26 57 -3 82 15 17 43 18 476 18 414 0 462 -2 479 -17z m2 -329 c33 -32 12 -87 -37 -99 -12 -3 -221 -4 -465 -3 -426 3 -444 4 -458 22 -18 26 -18 61 2 80 14 14 71 16 479 16 408 0 465 -2 479 -16z"/>
        <path d="M1910 3045 l0 -235 375 0 375 0 0 235 0 235 -375 0 -375 0 0 -235z"/>
        <path d="M285 2678 c-3 -7 -4 -553 -3 -1213 l3 -1200 510 0 510 0 0 1210 0 1210 -508 3 c-401 2 -509 0 -512 -10z m345 -437 c6 -13 10 -210 10 -570 l0 -550 -22 -15 c-31 -22 -45 -20 -73 9 l-25 24 0 538 c0 601 -2 583 65 583 24 0 38 -6 45 -19z m424 -3 c14 -20 16 -90 16 -564 l0 -543 -26 -20 c-33 -26 -49 -26 -74 -1 -20 20 -20 33 -20 569 0 486 2 551 16 565 23 24 70 20 88 -6z"/>
        <path d="M3262 1478 l3 -1213 510 0 510 0 0 1210 0 1210 -513 3 -512 2 2 -1212z m340 765 c17 -15 18 -46 18 -559 0 -298 -3 -550 -6 -559 -15 -37 -82 -38 -106 -1 -4 6 -8 256 -8 555 0 493 2 546 17 563 20 22 61 23 85 1z m435 -13 c16 -31 19 -1053 3 -1094 -14 -39 -53 -53 -85 -32 l-25 16 0 558 c0 432 3 561 12 570 22 22 81 10 95 -18z"/>
        <path d="M2040 580 l0 -320 245 0 245 0 0 320 0 320 -245 0 -245 0 0 -320z"/>
      </g>
    </svg>
  ),
];

// Componente para cada estadística con animación
const AnimatedStat = ({ value, suffix, label }) => {
  const { count, ref } = useCountUp(value, 2000);

  return (
    <div
      ref={ref}
      className="bg-black/40 border border-cloudima-blue/20 rounded-xl p-4 md:p-6 text-center backdrop-blur-sm"
    >
      <div className="text-2xl md:text-4xl font-bold text-white">
        {count}{suffix}
      </div>
      <div className="mt-2 text-xs md:text-sm text-gray-300">{label}</div>
    </div>
  );
};

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-4 h-4 text-cloudima-blue"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-2.59a.75.75 0 10-1.22-.86l-3.59 5.1-1.66-1.66a.75.75 0 10-1.06 1.06l2.25 2.25c.33.33.87.29 1.14-.09l5.14-5.77z"
      clipRule="evenodd"
    />
  </svg>
);

const ImpactAndAudience = () => {
  return (
    <section className="bg-indigo-900 pb-16 md:pb-24">
      <div className="container px-4 py-4 mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-16 md:mb-24">
          {stats.map((s, idx) => (
            <AnimatedStat
              key={idx}
              value={s.value}
              suffix={s.suffix}
              label={s.label}
            />
          ))}
        </div>

        {/* Who is Cloudima for? */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
          <h3 className="text-2xl md:text-4xl font-semibold text-white">Who is Cloudima for?</h3>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-gray-300">
            From freelancers to large enterprises, Cloudima adapts to your specific needs
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 items-stretch">
          {audiences.map((a, idx) => (
            <div
              key={idx}
              className="bg-black/50 border border-cloudima-blue/20 rounded-2xl p-5 md:p-6 hover:bg-black/60 transition-colors h-full flex flex-col"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-cloudima-blue/10 border border-cloudima-blue/20 flex items-center justify-center text-cloudima-blue mb-3 md:mb-4 mx-auto">
                {AudienceIcons[idx]}
              </div>
              <h4 className="text-lg md:text-xl font-semibold text-white">{a.title}</h4>
              <p className="mt-2 text-sm md:text-base text-gray-300">{a.description}</p>
              <ul className="mt-4 space-y-2">
                {a.points.map((p, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-200 text-sm md:text-base">
                    <CheckIcon />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImpactAndAudience;
